import { InviteStatus, getInviteStatus, getInviteStatusTooltip } from "@utils/providerInviteStatus";
import { tabToName } from "@utils/utils";
import React, { useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { Badge, Button, Headline, TabNavigation, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  ApiProviderMetadataInvitationStatusChoices,
  ProviderRoleEnums,
  useReissueProviderInvitationMutation,
} from "@api/graphql/types-and-hooks";
import {
  CopyToClipboardButton,
  CopyToClipboardButtonVariant,
} from "@components/CopyToClipboardButton/CopyToClipboardButton";
import { PageHeader, PageHeaderTabsContainer } from "@components/PageHeader/PageHeader";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { SendProviderInviteDialog } from "@pages/Members/ProviderDetail/SendProviderInviteDialog";
import { useProvidersData } from "@providers/ProvidersDataProvider";

enum ProviderDetailTab {
  Details = "details",
  Clients = "clients",
}

const providerDetailTabs = [ProviderDetailTab.Details, ProviderDetailTab.Clients];

export const ProviderDetail: React.FC = () => {
  const { pathname, state } = useLocation();
  const { providerDetailsQuery } = useProvidersData();
  const { data, isFetching } = providerDetailsQuery;

  const provider = data?.providerById;
  const providermetadata = provider?.providermetadata;
  const currentRoles = useMemo(
    () =>
      (provider?.organizationuserrolesSet.edges
        .map((edge) => edge?.node?.role)
        .filter(Boolean) as unknown as ProviderRoleEnums[]) ?? [],
    [provider?.organizationuserrolesSet.edges],
  );

  const [sendInviteDialogOpen, setSendInviteDialogOpen] = useState(false);

  const reissueInvitation = useReissueProviderInvitationMutation({
    onSuccess: (res) => {
      if (!res.reissueProviderInvitation?.status) {
        notifyError(res.reissueProviderInvitation?.message || "Something went wrong sending this invite!");
        return;
      }
      notifySuccess(`Invite sent to ${providermetadata?.email}`);
      providerDetailsQuery.refetch();
    },
    onError: () => {
      notifyError("Something went wrong sending this invite!");
    },
  });

  if (!provider || !providermetadata) {
    if (isFetching) return null;
    return (
      <div className="w-full">
        <Headline size="medium">Provider not found</Headline>
      </div>
    );
  }

  const name = `${providermetadata.firstName || ""} ${providermetadata.lastName || ""}`.trim();
  const portalStatus = providermetadata.invitationStatus ?? null;

  const statusBadge = () => {
    const badge = (() => {
      switch (portalStatus) {
        case ApiProviderMetadataInvitationStatusChoices.Accepted:
          return <Badge appearance="success" text="Active" />;
        case ApiProviderMetadataInvitationStatusChoices.Pending:
        case ApiProviderMetadataInvitationStatusChoices.Expired:
          return <Badge appearance="warn" text="Invited" />;
        case null:
          return <Badge appearance="neutral" text="Not invited" />;
      }
    })();

    const inviteStatus = getInviteStatus(portalStatus ?? "");
    return (
      <Tooltip
        showInfoIcon={false}
        disabled={inviteStatus !== InviteStatus.NotInvited && inviteStatus !== InviteStatus.Pending}
        text={getInviteStatusTooltip(inviteStatus)}
      >
        {badge}
      </Tooltip>
    );
  };

  const handleResendInvite = () => {
    reissueInvitation.mutate({ providerId: provider.id });
  };

  const inviteActions = () => {
    if (portalStatus === null) {
      return (
        <Button
          text="Send invite"
          variant="primary"
          buttonStyle="border"
          onClick={() => setSendInviteDialogOpen(true)}
          disabled={reissueInvitation.isPending}
        />
      );
    }

    if (
      portalStatus === ApiProviderMetadataInvitationStatusChoices.Pending ||
      portalStatus === ApiProviderMetadataInvitationStatusChoices.Expired
    ) {
      return (
        <div className="flex items-baseline gap-3">
          <CopyToClipboardButton
            text={providermetadata.invitationUrl || ""}
            variant={CopyToClipboardButtonVariant.Text}
            label="Copy Invite Link"
          />
          <Button
            text="Resend invite"
            variant="primary"
            buttonStyle="border"
            onClick={handleResendInvite}
            disabled={reissueInvitation.isPending}
          />
        </div>
      );
    }

    return undefined;
  };

  return (
    <div className="flex h-full w-full flex-col">
      <PageHeader
        breadcrumbs={[
          { label: "User Management", href: "/members" },
          { label: "Providers", href: "/members" },
          { label: name },
        ]}
        title={name}
        meta={statusBadge()}
        actions={inviteActions()}
      />
      <PageHeaderTabsContainer>
        <TabNavigation
          tabs={providerDetailTabs.map((tab) => ({
            current: pathname.split("/")[pathname.split("/").length - 1] === tab,
            type: "component",
            name: tab,
            Component: (
              <Link to={tab} state={state}>
                {tabToName(tab)}
              </Link>
            ),
          }))}
        />
      </PageHeaderTabsContainer>

      <SendProviderInviteDialog
        isOpen={sendInviteDialogOpen}
        onClose={() => setSendInviteDialogOpen(false)}
        providerId={provider.id}
        providerEmail={providermetadata.email || ""}
        currentRoles={currentRoles}
        onSent={() => providerDetailsQuery.refetch()}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="py-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
